import type { IssueBusinessContext } from '../diagnostics/issue-package'
import type { TestSessionAction, TestSessionDiagnostic, TestSessionProblem, TestSessionRequest, TestSessionSnapshot } from './test-session-types'
import { sanitizeText, sanitizeUrl } from '../diagnostics/format-diagnostic'

type Options = { maxActions?: number; maxRequests?: number; maxDiagnostics?: number }

function sanitizeContext(context: IssueBusinessContext | undefined): IssueBusinessContext | undefined {
  if (!context) return undefined
  const userAccount = context.userAccount
    ? (context.userAccount.includes('*') ? sanitizeText(context.userAccount) : `${context.userAccount.slice(0, 2) || '*'}***`)
    : undefined
  const sanitized = {
    systemCode: context.systemCode ? sanitizeText(context.systemCode) : undefined,
    menuTrail: context.menuTrail?.map(sanitizeText),
    operations: context.operations?.map(sanitizeText),
    userAccount,
    roles: context.roles?.map(sanitizeText),
  }
  return Object.values(sanitized).some((value) => value && (!Array.isArray(value) || value.length > 0)) ? sanitized : undefined
}

function sanitizeAction(action: TestSessionAction): TestSessionAction {
  return {
    ...action,
    route: sanitizeUrl(action.route),
    label: action.label ? sanitizeText(action.label) : undefined,
    target: action.target && {
      tag: sanitizeText(action.target.tag),
      testId: action.target.testId ? sanitizeText(action.target.testId) : undefined,
      dataComponent: action.target.dataComponent ? sanitizeText(action.target.dataComponent) : undefined,
      role: action.target.role ? sanitizeText(action.target.role) : undefined,
    },
  }
}

function sanitizeRequest(request: TestSessionRequest): TestSessionRequest {
  return {
    ...request,
    route: sanitizeUrl(request.route),
    path: sanitizeUrl(request.path),
    responseCode: request.responseCode ? sanitizeText(request.responseCode) : undefined,
    responseMessage: request.responseMessage ? sanitizeText(request.responseMessage) : undefined,
    traceId: request.traceId ? sanitizeText(request.traceId) : undefined,
    input: request.input && {
      queryKeys: request.input.queryKeys?.map(sanitizeText),
      body: request.input.body?.map(sanitizeText),
    },
    trigger: request.trigger ? sanitizeAction(request.trigger) : undefined,
  }
}

function sanitizeDiagnostic(diagnostic: TestSessionDiagnostic): TestSessionDiagnostic {
  return {
    ...diagnostic,
    route: sanitizeUrl(diagnostic.route),
    error: { name: sanitizeText(diagnostic.error.name), message: sanitizeText(diagnostic.error.message) },
    request: diagnostic.request && {
      ...diagnostic.request,
      method: sanitizeText(diagnostic.request.method),
      path: sanitizeUrl(diagnostic.request.path),
      responseCode: diagnostic.request.responseCode ? sanitizeText(diagnostic.request.responseCode) : undefined,
      responseMessage: diagnostic.request.responseMessage ? sanitizeText(diagnostic.request.responseMessage) : undefined,
      traceId: diagnostic.request.traceId ? sanitizeText(diagnostic.request.traceId) : undefined,
      input: diagnostic.request.input && {
        queryKeys: diagnostic.request.input.queryKeys?.map(sanitizeText),
        body: diagnostic.request.input.body?.map(sanitizeText),
      },
    },
  }
}

export function createTestSessionStore({ maxActions = 100, maxRequests = 100, maxDiagnostics = 30 }: Options = {}) {
  let snapshot: TestSessionSnapshot = { active: false, actions: [], requests: [] }
  const listeners = new Set<() => void>()
  const emit = () => listeners.forEach((listener) => listener())

  return {
    start({ at = Date.now(), route, build, context }: { at?: number; route: string; build: { commit: string; mode: string }; context?: IssueBusinessContext }) {
      const safeRoute = sanitizeUrl(route)
      snapshot = { active: true, startedAt: at, initialRoute: safeRoute, currentRoute: safeRoute, build, context: sanitizeContext(context), actions: [], requests: [], diagnostics: [], problems: [] }
      emit()
    },
    end(at = Date.now(), route?: string) {
      if (!snapshot.active) return
      snapshot = { ...snapshot, active: false, endedAt: at, currentRoute: route ? sanitizeUrl(route) : snapshot.currentRoute }
      emit()
    },
    recordAction(action: TestSessionAction) {
      if (!snapshot.active) return
      const safeAction = sanitizeAction(action)
      snapshot = { ...snapshot, currentRoute: safeAction.route, actions: [...snapshot.actions, safeAction].slice(-maxActions) }
      emit()
    },
    recordRequest(request: TestSessionRequest) {
      if (!snapshot.active) return
      const safeRequest = sanitizeRequest(request)
      snapshot = { ...snapshot, currentRoute: safeRequest.route, requests: [...snapshot.requests, safeRequest].slice(-maxRequests) }
      emit()
    },
    recordDiagnostic(diagnostic: TestSessionDiagnostic) {
      if (!snapshot.active) return
      const safeDiagnostic = sanitizeDiagnostic(diagnostic)
      snapshot = { ...snapshot, currentRoute: safeDiagnostic.route, diagnostics: [...(snapshot.diagnostics ?? []), safeDiagnostic].slice(-maxDiagnostics) }
      emit()
    },
    markProblem(problem: { at?: number; expected: string; actual: string }) {
      if (!snapshot.startedAt) return
      const at = problem.at ?? Date.now()
      const action = [...snapshot.actions].reverse().find((candidate) => candidate.at <= at)
      const request = [...snapshot.requests].reverse().find((candidate) => candidate.at <= at)
      const marker: TestSessionProblem = {
        id: `problem-${at}-${(snapshot.problems?.length ?? 0) + 1}`,
        at,
        route: action?.route ?? request?.route ?? snapshot.currentRoute ?? snapshot.initialRoute ?? '/',
        expected: sanitizeText(problem.expected),
        actual: sanitizeText(problem.actual),
        action,
        request,
      }
      snapshot = { ...snapshot, problems: [...(snapshot.problems ?? []), marker] }
      emit()
    },
    getSnapshot() {
      return snapshot
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

export const testSessionStore = createTestSessionStore()
